/// FOURJS_START_COPYRIGHT(D,2016)
/// Property of Four Js*
/// (c) Copyright Four Js 2016, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

import { BrowserMultiFormatReader as reader, BarcodeFormat } from "@zxing/browser";

modulum("BarcodeScannerWidget", ["ModalWidget", "WidgetFactory"], function (context, cls) {
  /**
   * @class BarcodeScannerWidget
   * @memberOf classes
   * @extends classes.ModalWidget
   */
  cls.BarcodeScannerWidget = context.oo.Class(cls.ModalWidget, function ($super) {
    return /** @lends classes.BarcodeScannerWidget.prototype */ {
      __name: "BarcodeScannerWidget",
      __templateName: "ModalWidget",

      $static: {
        /**
         * @static
         * Reverse enum for zxing BarcodeFormat
         * @readonly
         * @enum {string};
         */
        BarcodeFormatName: Object.fromEntries(Object.entries(window.ZXing?.BarcodeFormat || BarcodeFormat).map(([key, value]) => [value, key])),
        /**
         * @static
         * Expected formats that can be given as an option of the frontcall
         * If not provided, the format is UTF-8
         * @readonly
         * @enum {string};
         */
        OutputFormat: {
          BASE64: "base64",
          HEXADECIMAL: "hexadecimal"
        },
        /**
         * @static
         * Cache value for the deviceID that can autofocus
         * See this.findBestCamera
         */
        bestCameraId: null
      },

      /**
       * HTML Element holding the video stream
       */
      _videoDom: null,
      /**
       * Handle for the barcode scanner
       */
      _controls: null,

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);
        const dialogContents = document.createElement("div");
        const headerTitleDom = document.createElement("span");
        headerTitleDom.innerHTML = "Barcode Scanner";

        this._videoDom = document.createElement("video");

        this._videoDom.autoplay = true;
        dialogContents.appendChild(this._videoDom);

        this.setHeader(headerTitleDom);
        this.setClosable(true);
        this.setContent(dialogContents);

        this.when(
          context.constants.widgetEvents.modalOut,
          function () {
            this.hide();
          }.bind(this)
        );
        this.when(
          context.constants.widgetEvents.close,
          function () {
            this.hide();
          }.bind(this)
        );
      },

      /**
       * Attempts to find the back camera with autofocus feature
       * in order to avoid the default wide-angle camera which cannot focus
       * @returns {Promise<string|null>} the deviceID of the autofocus back camera, null if not found
       */
      findBestCamera: async function () {
        if (cls.BarcodeScannerWidget.bestCameraId) return cls.BarcodeScannerWidget.bestCameraId;

        const videoInputDevices = await navigator.mediaDevices.enumerateDevices();
        for (let videoDevice of videoInputDevices) {
          if (cls.BarcodeScannerWidget.bestCameraId) break;
          if (!videoDevice.label?.toLowerCase().includes("back")) {
            continue;
          }
          try {
            // Need to open a hidden stream in order to extract its features
            const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: videoDevice.deviceId } });
            const track = stream.getVideoTracks()[0];
            const settings = track.getCapabilities();
            if (settings.focusMode && settings.focusMode.includes("continuous")) {
              cls.BarcodeScannerWidget.bestCameraId = videoDevice.deviceId;
            }
            track.stop();
          } catch {}
        }

        return cls.BarcodeScannerWidget.bestCameraId;
      },

      /**
       * Opens the video stream and watches until a code is found
       * @param {string} outputFormat can be "base64" or "hexadecimal", utf-8 otherwise
       * @param onDetect result callback which expects data and format as a parameter
       * @returns {Promise<void>}
       */
      detectBarcode: async function (outputFormat, onDetect) {
        const deviceId = await this.findBestCamera();
        const barcodeDetector = window.ZXingBrowser ? new window.ZXingBrowser.BrowserMultiFormatReader() : new reader();
        const constraints = {
          audio: false,
          video: {
            focusMode: "continuous",
            advanced: [{ zoom: 2.0 }],
            ...(deviceId ? { deviceId: deviceId } : { facingMode: { ideal: "environment" } })
          }
        };

        this._controls = await barcodeDetector.decodeFromConstraints(constraints, this._videoDom, (result, error, controls) => {
          if (result) {
            const format = cls.BarcodeScannerWidget.BarcodeFormatName[result.getBarcodeFormat()];
            const rawBytes = result.getRawBytes();
            if (rawBytes && outputFormat === cls.BarcodeScannerWidget.OutputFormat.HEXADECIMAL) {
              result.getRawBytes().toHex ? onDetect(result.getRawBytes().toHex(), format) : onDetect(null, format);
            } else if (rawBytes && outputFormat === cls.BarcodeScannerWidget.OutputFormat.BASE64) {
              result.getRawBytes().toBase64 ? onDetect(result.getRawBytes().toBase64(), format) : onDetect(null, format);
            } else {
              onDetect(result.getText(), format);
            }
            controls.stop();
          }
        });
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._controls) {
          this._controls.stop();
        }
        if (this._videoDom) {
          if (this._videoDom.srcObject) {
            this._videoDom.srcObject = null;
          }
          this._videoDom = null;
        }
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      show: async function () {
        $super.show.call(this);
        this.resizeHandler();
      },

      /**
       * hides the dialog
       * @inheritDoc
       */
      hide: function () {
        if (this._controls) {
          this._controls.stop();
        }
        if (this._videoDom) {
          this._videoDom.srcObject = null;
          this._videoDom = null;
        }
        this._resetAsMoved();
        $super.hide.call(this);
      }
    };
  });
  cls.WidgetFactory.registerBuilder("BarcodeScanner", cls.BarcodeScannerWidget);
});
